import React, { useState, useEffect, useRef } from "react";
import {
  Check,
  Image as ImageIcon,
  Fingerprint,
  Loader2,
  AlertTriangle,
  Mic,
  Sparkles,
  Calendar as CalIcon,
  Mail,
  Camera,
  Wrench,
  ClipboardCheck,
} from "lucide-react";
import Logo from "@/components/Logo";

/**
 * PhoneMockup
 *
 * CSS-only phone frame that cycles through four product surfaces every
 * five seconds, pausing on hover. Used on the Pricing page next to the
 * "Upload straight from your phone" callout. Screens cover the journey
 * a renter sees after they tap upload:
 *   1. Vault upload in flight
 *   2. Deep Read flags returned by Claude
 *   3. Voice Note transcribed and saved
 *   4. Chronology timeline auto-built from documents
 *
 * Built with no image assets so it stays sharp at any size and matches
 * the brand palette automatically.
 */
const SCREENS = [
  { id: "upload", label: "Upload" },
  { id: "deepread", label: "Deep Read" },
  { id: "voice", label: "Voice" },
  { id: "timeline", label: "Chronology" },
];

export default function PhoneMockup() {
  const [active, setActive] = useState("upload");
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(() => {
      setActive((cur) => {
        const idx = SCREENS.findIndex((s) => s.id === cur);
        return SCREENS[(idx + 1) % SCREENS.length].id;
      });
    }, 5000);
    return () => clearInterval(timer.current);
  }, [paused]);

  return (
    <div
      className="relative mx-auto"
      style={{ width: "240px" }}
      data-testid="phone-mockup"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Phone body — chrome bezel */}
      <div
        className="relative bg-[#0D2B52] p-2.5"
        style={{
          borderRadius: "36px",
          boxShadow:
            "0 25px 50px -15px rgba(13, 43, 82, 0.35), 0 6px 12px rgba(0,0,0,0.05)",
        }}
      >
        {/* Screen */}
        <div
          className="bg-[#FAF9F6] overflow-hidden relative"
          style={{ borderRadius: "26px", height: "480px" }}
        >
          {/* Status bar */}
          <div className="flex items-center justify-between px-5 pt-3 pb-1.5">
            <span className="font-mono text-[10px] text-[#0D2B52] font-semibold">9:41</span>
            <div className="flex items-center gap-1 text-[#0D2B52]">
              <span className="font-mono text-[9px]">●●●○</span>
              <span className="font-mono text-[9px] ml-1">73%</span>
            </div>
          </div>

          {/* Dynamic island */}
          <div
            className="absolute top-2 left-1/2 -translate-x-1/2 bg-black rounded-full"
            style={{ width: "70px", height: "20px" }}
            aria-hidden="true"
          />

          {/* App header */}
          <div className="flex items-center gap-2 px-5 pt-3 pb-2 border-b border-[#E5E4DE]">
            <Logo size={18} />
            <div className="font-serif text-[12px] text-[#0D2B52]">TenantTrail</div>
            <div className="ml-auto font-mono text-[8px] uppercase tracking-[0.1em] text-[#9CA3AF]">
              QLD
            </div>
          </div>

          {/* Screen content */}
          <div className="px-5 pt-3 pb-3" style={{ height: "388px", overflow: "hidden" }}>
            {active === "upload" && <UploadScreen />}
            {active === "deepread" && <DeepReadScreen />}
            {active === "voice" && <VoiceScreen />}
            {active === "timeline" && <ChronologyScreen />}
          </div>

          {/* Progress dots */}
          <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-1.5" data-testid="phone-mockup-dots">
            {SCREENS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                aria-label={`Show ${s.label}`}
                className={`h-1 transition-all ${
                  active === s.id ? "w-5 bg-[#1B6C62]" : "w-1 bg-[#0D2B52]/25"
                }`}
                data-testid={`phone-mockup-dot-${s.id}`}
              />
            ))}
          </div>

          {/* Home indicator */}
          <div
            className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-[#0D2B52]/30 rounded-full"
            style={{ width: "100px", height: "3px" }}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Subtle reflection / floor shadow */}
      <div
        className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#0D2B52]/10 blur-md"
        style={{ width: "180px", height: "8px", borderRadius: "50%" }}
        aria-hidden="true"
      />
    </div>
  );
}

/* ----- Inner screens ----- */

function UploadScreen() {
  return (
    <div className="fade-in" data-testid="phone-screen-upload">
      <div className="font-mono text-[8.5px] uppercase tracking-[0.15em] text-[#1B6C62] mb-0.5">
        Documents · 12 Marlow Cres
      </div>
      <div className="font-serif text-[14px] text-[#0D2B52]">Add to your case</div>

      {/* Drag/drop tile */}
      <div className="mt-3 border-2 border-dashed border-[#1B6C62]/40 bg-[#E6F5F2]/30 px-3 py-3 flex items-center gap-2">
        <ImageIcon size={14} className="text-[#1B6C62] shrink-0" strokeWidth={1.5} />
        <div className="text-[10px] text-[#0D2B52] leading-tight">
          Drop or pick photos
          <br />
          <span className="text-[#6B7280]">iPhone, Android, anything</span>
        </div>
      </div>

      {/* Upload progress card */}
      <div className="mt-3 bg-white border border-[#E5E4DE] px-3 py-2.5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 bg-[#FAF6E8] flex items-center justify-center shrink-0">
            <Loader2 size={12} className="text-[#8A6D3B] animate-spin" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-[#1A1A1A] truncate font-medium">IMG_4821.HEIC</div>
            <div className="font-mono text-[8px] text-[#6B7280]">
              Converting · 38% · 12 of 32
            </div>
          </div>
        </div>
        <div className="h-1 bg-[#E5E4DE] overflow-hidden">
          <div className="h-full bg-[#1B6C62]" style={{ width: "38%" }} />
        </div>
      </div>

      {/* Just-finished file with hash badge */}
      <div className="mt-2 bg-white border border-[#E5E4DE] px-3 py-2 flex items-center gap-2">
        <div className="w-7 h-7 bg-[#E6F5F2] flex items-center justify-center shrink-0">
          <Check size={12} className="text-[#1B6C62]" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-[#1A1A1A] truncate font-medium">kitchen-leak.jpg</div>
          <div className="font-mono text-[8px] text-[#6B7280]">14 Mar · 22:08 · EXIF preserved</div>
        </div>
        <Fingerprint size={11} className="text-[#1B6C62] shrink-0" strokeWidth={1.75} />
      </div>

      {/* Toast — success */}
      <div className="mt-3 bg-[#0D2B52] text-white px-3 py-2 flex items-center gap-2">
        <Check size={11} className="text-[#9DD6CB] shrink-0" strokeWidth={2.5} />
        <div className="text-[9.5px] leading-tight">
          <div className="font-medium">Saved to your vault</div>
          <div className="text-[#9DD6CB] font-mono text-[8px]">All dates locked in</div>
        </div>
      </div>
    </div>
  );
}

function DeepReadScreen() {
  return (
    <div className="fade-in" data-testid="phone-screen-deepread">
      <div className="font-mono text-[8.5px] uppercase tracking-[0.15em] text-[#1B6C62] mb-0.5 flex items-center gap-1">
        <Sparkles size={9} strokeWidth={1.75} /> Deep Read · Lease.pdf
      </div>
      <div className="font-serif text-[14px] text-[#0D2B52] leading-tight">
        Three things to watch
      </div>

      <div className="mt-3 space-y-2">
        <FindingRow
          tone="warn"
          title="Pro carpet cleaning required"
          body="Clause 24. In QLD this is not enforceable without proof of damage."
        />
        <FindingRow
          tone="warn"
          title="Rent increase every 6 months"
          body="Clause 11.2. Minimum interval in QLD is 12 months."
        />
        <FindingRow
          tone="ok"
          title="Bond lodged with RTA"
          body="Receipt #B-77421 matches lease amount."
        />
      </div>

      <div className="mt-3 bg-[#FAF6E8] border-l-2 border-[#8A6D3B] px-2.5 py-1.5">
        <div className="font-mono text-[7.5px] uppercase tracking-[0.1em] text-[#8A6D3B] mb-0.5">
          Compass
        </div>
        <div className="text-[9.5px] text-[#1A1A1A] leading-snug">
          Want a polite reply to the agent about clause 24?
        </div>
      </div>
    </div>
  );
}

function FindingRow({ tone, title, body }) {
  const accent = tone === "warn" ? "#8A6D3B" : "#1B6C62";
  const bg = tone === "warn" ? "#FAF6E8" : "#E6F5F2";
  const Icon = tone === "warn" ? AlertTriangle : Check;
  return (
    <div className="bg-white border border-[#E5E4DE] px-2.5 py-1.5 flex items-start gap-2">
      <div
        className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: bg }}
      >
        <Icon size={10} strokeWidth={2} style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] text-[#0D2B52] font-medium leading-tight">{title}</div>
        <div className="text-[9px] text-[#6B7280] leading-snug">{body}</div>
      </div>
    </div>
  );
}

function VoiceScreen() {
  return (
    <div className="fade-in" data-testid="phone-screen-voice">
      <div className="font-mono text-[8.5px] uppercase tracking-[0.15em] text-[#1B6C62] mb-0.5 flex items-center gap-1">
        <Mic size={9} strokeWidth={1.75} /> Voice Note · just now
      </div>
      <div className="font-serif text-[14px] text-[#0D2B52] leading-tight">
        Recorded after the inspection
      </div>

      {/* Waveform */}
      <div className="mt-3 bg-white border border-[#E5E4DE] px-3 py-3 flex items-end gap-0.5 h-14">
        {[3, 6, 8, 5, 10, 12, 8, 14, 10, 6, 12, 8, 4, 9, 11, 7, 5, 10, 14, 11, 8, 4, 7, 9, 6].map(
          (h, i) => (
            <div
              key={i}
              className="flex-1 bg-[#1B6C62]"
              style={{ height: `${h * 3}px`, opacity: 0.4 + (h / 14) * 0.6 }}
            />
          )
        )}
      </div>

      {/* Transcript */}
      <div className="mt-2 bg-[#E6F5F2] border-l-2 border-[#1B6C62] px-2.5 py-2">
        <div className="font-mono text-[7.5px] uppercase tracking-[0.1em] text-[#1B6C62] mb-0.5">
          Transcript
        </div>
        <div className="text-[10px] text-[#1A1A1A] leading-snug">
          Agent showed up at 4:15pm, ten minutes early. Took photos of the bathroom but skipped
          the back room. Said the mould was &ldquo;cosmetic&rdquo;.
        </div>
      </div>

      <div className="mt-2 flex items-center gap-1.5">
        <Check size={10} className="text-[#1B6C62]" strokeWidth={2.5} />
        <div className="font-mono text-[8.5px] text-[#6B7280]">
          Saved · timestamped · added to chronology
        </div>
      </div>
    </div>
  );
}

function ChronologyScreen() {
  const events = [
    { tag: "Today", title: "Voice note", body: "Re: inspection", Icon: Mic },
    { tag: "3 days ago", title: "Deep Read", body: "Lease flagged 2 issues", Icon: Sparkles },
    { tag: "1 wk ago", title: "Repair logged", body: "Heater, acknowledged", Icon: Wrench },
    { tag: "2 wks ago", title: "Photos added", body: "Bathroom mould, 6 imgs", Icon: Camera },
    { tag: "Aug 2025", title: "Routine inspection", body: "Disagreed on 2 items", Icon: ClipboardCheck },
  ];
  return (
    <div className="fade-in relative" data-testid="phone-screen-chronology">
      <div className="font-mono text-[8.5px] uppercase tracking-[0.15em] text-[#1B6C62] mb-0.5 flex items-center gap-1">
        <CalIcon size={9} strokeWidth={1.75} /> Chronology · tribunal-ready
      </div>
      <div className="font-serif text-[14px] text-[#0D2B52] leading-tight mb-2">
        Every event, in order.
      </div>
      <div className="absolute left-[13px] top-[60px] bottom-3 w-px bg-[#E5E4DE]" aria-hidden="true" />
      <ul className="space-y-2">
        {events.map((e, i) => (
          <li key={i} className="flex items-start gap-2 relative">
            <div className="p-1 bg-[#FAF9F6] border border-[#E5E4DE] text-[#1B6C62] relative z-10 mt-0.5">
              <e.Icon size={9} strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[7.5px] uppercase tracking-[0.1em] text-[#9CA3AF]">
                {e.tag}
              </div>
              <div className="text-[10px] text-[#1A1A1A] leading-snug">{e.title}</div>
              <div className="text-[9px] text-[#6B7280] leading-snug truncate">{e.body}</div>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center gap-1.5">
        <Mail size={10} className="text-[#1B6C62]" strokeWidth={1.75} />
        <div className="font-mono text-[8.5px] text-[#6B7280]">
          Export PDF bundle, ready to send
        </div>
      </div>
    </div>
  );
}
